import { Button, Grid, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { Global_CTX } from "../../contexts/global-ctx";
import { useDebounce } from "../../hooks/useDebounce";
import {
    Branch_Service,
    Get_Branches_Filter,
} from "../../services/branch/branch-service";
import { PERMISSIONS } from "../../utils/constants";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { dateFormatter } from "../constants";
import { Search } from "../filters/Search";
import New_Item from "../new-item";
import { RUDTable } from "../Table/RUDTable";
import UserContext, { User_Type } from "../UserContext";
import Branch_Form from "./create-branch-form";

const initial_filter: Omit<Get_Branches_Filter, "page" | "limit"> & {
    organization: string;
    apiKey: string;
} = {
    organization: "",
    apiKey: "",
};

export default function Branches_Page() {
    const [filter, set_filter] = useState(initial_filter);
    const { user } = useContext(UserContext);
    const { refetch_counter, set_refetch_counter } = useContext(Global_CTX);
    const debounce = useDebounce();

    function handle_search(event: ChangeEvent<HTMLInputElement>) {
        const { value } = event.target;

        set_filter((f) => ({
            ...f,
            search: value,
        }));

        debounce(() => set_refetch_counter((n) => n + 1));
    }

    useEffect(() => {
        if (user?.user_type === User_Type.Admin) {
            set_filter((f) => ({
                ...f,
                organization: user.organization.code,
                apiKey: user.organization.apiKey[0].key,
            }));
        }
    }, [user?.user_type]);

    return (
        <Box>
            <Typography
                pt={2}
                px={2}
                color={(theme) => theme.palette.primary.main}
                variant="h5"
            >
                Branches
            </Typography>

            {user?.user_type === User_Type.Superadmin && (
                <New_Item title="Create Branches">
                    <Branch_Form />
                </New_Item>
            )}

            <Grid container p={2} gap={2}>
                {user?.user_type === User_Type.Superadmin && (
                    <Grid item xs={12} sm={6} lg={3}>
                        <Autocomplete
                            label="Organization"
                            labelKey="name"
                            api="/organization"
                            setOutput={(org) => {
                                set_filter((f) => ({
                                    ...f,
                                    organization: org?.code || "",
                                }));
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} lg={3}>
                    <Search filter={filter} setFilter={set_filter} />
                </Grid>

                <Grid item xs={12} sm={6} lg={3}>
                    <Button
                        color="primary"
                        fullWidth
                        variant="contained"
                        onClick={() =>
                            set_filter({
                                ...filter,
                                apply: (filter.apply ?? 0) + 1,
                            })
                        }
                        style={{ height: "100%" }}
                        size="small"
                    >
                        {" "}
                        Search
                    </Button>
                </Grid>
            </Grid>

            <RUDTable
                storageKey="branches-page"
                updatingAgents={[filter.apply, filter.organization]}
                getter={(page, limit) => {
                    const handler =
                        user?.user_type === User_Type.Admin
                            ? Branch_Service.get_branches
                            : Branch_Service.get_superadmin_branches;

                    return handler(
                        {
                            ...filter,
                            page,
                            limit,
                        },
                        filter.organization,
                        filter.apiKey
                    );
                }}
                deletor={(ids) => {
                    return Branch_Service.delete_branches(
                        ids as any,
                        filter.organization
                    );
                }}
                editor={(id, updated_org) => {
                    return Branch_Service.edit_branch(
                        id,
                        updated_org,
                        filter.organization,
                        user?.user_type == User_Type.Admin ? filter.apiKey : ""
                    );
                }}
                rowsPreprocessor={(row) => ({
                    ...row,
                    deletionId: row.id,
                    id: row.id,
                    org_name: row?.organization?.name ?? "N / A",
                    default_org: { code: "123", name: "Default Org" },
                    organization: "",
                    createdAt: dateFormatter.format(new Date(row.createdAt)),
                })}
                ops={{
                    read: PERMISSIONS.READ_BRANCH, // Set value for this permissions
                    edit:
                        user?.user_type === User_Type.Admin
                            ? PERMISSIONS.UPDATE_BRANCH
                            : "",
                    delete:
                        user?.user_type === User_Type.Admin
                            ? PERMISSIONS.ARCHIVE_BRANCH
                            : "",
                }}
                readables={
                    user?.user_type === User_Type.Superadmin
                        ? {
                              id: "Id",
                              name: "Name",
                              org_name: "Organization",
                          }
                        : {
                              id: "Id",
                              createdAt: "Created At",
                              name: "Name",
                          }
                }
                editables={[
                    {
                        isRequired: true,
                        label: "Name",
                        name: "name",
                        type: "textfield",
                    },
                    // {
                    //     isRequired: true,
                    //     label: "Organization",
                    //     name: "organization",
                    //     type: "autocomplete",
                    //     autocomplete: {
                    //         api: "/organization",
                    //         labelKey: "name",
                    //         label: "Organization",
                    //         defaultValue: "default_org",
                    //         preprocessor: (org) => ({
                    //             ...org,
                    //             id: org.code,
                    //         }),
                    //     },
                    // },
                ]}
            />
        </Box>
    );
}
